import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { cardImagePath } from '@/lib/cardImagePath';

/**
 * Major Arcana card — ornate SVG visual + symbolic centerpiece per card.
 *
 * Why all-SVG: works inside Telegram on iOS/Android with no asset loading,
 * scales infinitely, theme-able via currentColor. Costs ~0 KB to ship.
 *
 * Each card has:
 *   • Number (Roman numeral) at top
 *   • Ornate gold border with corner flourishes
 *   • Central symbol (unique per card)
 *   • Name in serif at bottom
 *   • Subtle starfield background pattern
 *
 * Cards we have custom art for: The Fool, The Magician, The High Priestess,
 * The Empress, The Emperor, The Lovers, The Star, The Moon, The Sun,
 * The Tower, Wheel of Fortune, Death. For others we fall back to a generic
 * geometric mandala that still looks intentional.
 */

interface CardDto {
  name_ru?: string;
  name_en?: string;
  orientation?: 'upright' | 'reversed';
}

interface Props {
  card: unknown;
  size?: number;
  /** Skip the built-in 3D flip-in animation. Use when the card sits
   *  inside a FlippableCard wrapper that owns the flip — otherwise both
   *  animations fire and the card visibly spins twice. */
  noIntro?: boolean;
}

const ROMAN: Record<string, string> = {
  'The Fool':           '0',
  'The Magician':       'I',
  'The High Priestess': 'II',
  'The Empress':        'III',
  'The Emperor':        'IV',
  'The Hierophant':     'V',
  'The Lovers':         'VI',
  'The Chariot':        'VII',
  'Strength':           'VIII',
  'The Hermit':         'IX',
  'Wheel of Fortune':   'X',
  'Justice':            'XI',
  'The Hanged Man':     'XII',
  'Death':              'XIII',
  'Temperance':         'XIV',
  'The Devil':          'XV',
  'The Tower':          'XVI',
  'The Star':           'XVII',
  'The Moon':           'XVIII',
  'The Sun':            'XIX',
  'Judgement':          'XX',
  'The World':          'XXI',
};


function CardArt({ name }: { name?: string }) {
  // Each art block is centred in a 100×100 SVG viewport.
  switch (name) {
    case 'The Sun':
      return (
        <g>
          <circle cx="50" cy="50" r="14" fill="#f5d97a" />
          <circle cx="50" cy="50" r="18" fill="none" stroke="#e8c356" strokeWidth="0.8" />
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i * Math.PI) / 6;
            const x1 = 50 + Math.cos(a) * 22;
            const y1 = 50 + Math.sin(a) * 22;
            const x2 = 50 + Math.cos(a) * 32;
            const y2 = 50 + Math.sin(a) * 32;
            return (
              <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke="#e8c356" strokeWidth="1.4" strokeLinecap="round" />
            );
          })}
        </g>
      );

    case 'The Moon':
      return (
        <g>
          <circle cx="50" cy="50" r="20" fill="#f5d97a" opacity="0.15" />
          <path d="M 60 35 A 18 18 0 1 0 60 65 A 14 14 0 1 1 60 35"
                fill="#f5d97a" />
          {/* Drops */}
          <circle cx="40" cy="76" r="1.2" fill="#e8c356" />
          <circle cx="45" cy="80" r="0.9" fill="#e8c356" />
          <circle cx="35" cy="82" r="0.9" fill="#e8c356" />
        </g>
      );

    case 'The Star':
      return (
        <g>
          {/* Main 8-point star */}
          <g transform="translate(50 45)">
            <path d="M0 -20 L 4 -4 L 20 0 L 4 4 L 0 20 L -4 4 L -20 0 L -4 -4 Z"
                  fill="#f5d97a" />
          </g>
          {/* Smaller stars */}
          {[
            { x: 25, y: 30, s: 0.4 },
            { x: 75, y: 25, s: 0.35 },
            { x: 78, y: 55, s: 0.3 },
            { x: 30, y: 65, s: 0.35 },
          ].map((s, i) => (
            <g key={i} transform={`translate(${s.x} ${s.y}) scale(${s.s})`}>
              <path d="M0 -10 L 2 -2 L 10 0 L 2 2 L 0 10 L -2 2 L -10 0 L -2 -2 Z"
                    fill="#e8c356" />
            </g>
          ))}
          {/* Water flow */}
          <path d="M30 78 Q 50 72 70 78" stroke="#e8c356" strokeWidth="0.8" fill="none" opacity="0.7" />
        </g>
      );

    case 'The Tower':
      return (
        <g stroke="#e8c356" strokeWidth="1" fill="none">
          {/* Tower body */}
          <rect x="40" y="35" width="20" height="40" fill="#1e1740" />
          {/* Crown */}
          <path d="M 38 35 L 42 28 L 50 35 L 58 28 L 62 35" />
          {/* Window */}
          <rect x="47" y="48" width="6" height="8" fill="#0b0820" />
          {/* Lightning bolt */}
          <path d="M 52 18 L 47 30 L 53 30 L 48 38" stroke="#f5d97a" strokeWidth="1.6" />
          {/* Cracks */}
          <path d="M 42 50 L 46 60 L 42 70" opacity="0.6" />
        </g>
      );

    case 'The Lovers':
      return (
        <g>
          {/* Two crowned heart-circles */}
          <circle cx="38" cy="45" r="9" fill="#e87fb1" />
          <circle cx="62" cy="45" r="9" fill="#f5d97a" />
          {/* Sun above */}
          <circle cx="50" cy="25" r="5" fill="#f5d97a" />
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * Math.PI) / 4;
            return (
              <line key={i}
                    x1={50 + Math.cos(a) * 7}
                    y1={25 + Math.sin(a) * 7}
                    x2={50 + Math.cos(a) * 10}
                    y2={25 + Math.sin(a) * 10}
                    stroke="#f5d97a" strokeWidth="0.8" />
            );
          })}
          {/* Heart joining */}
          <path d="M 50 65 L 44 58 Q 40 54 44 50 Q 50 50 50 56 Q 50 50 56 50 Q 60 54 56 58 Z"
                fill="#e87fb1" />
        </g>
      );

    case 'Death':
      return (
        <g stroke="#e8c356" strokeWidth="1" fill="none">
          {/* Scythe handle */}
          <line x1="35" y1="80" x2="65" y2="22" strokeWidth="1.4" />
          {/* Blade */}
          <path d="M 65 22 Q 78 22 78 36" strokeWidth="1.6" />
          {/* Phoenix-skull abstracted */}
          <g transform="translate(38 60)">
            <circle r="6" fill="#1e1740" />
            <circle cx="-2" cy="-1" r="1.2" fill="#0b0820" />
            <circle cx="2" cy="-1" r="1.2" fill="#0b0820" />
            <path d="M -3 3 L 3 3" />
          </g>
          {/* Rose for rebirth */}
          <g transform="translate(70 70)">
            <circle r="2" fill="#e87fb1" />
            <circle r="4" fill="none" stroke="#e87fb1" />
          </g>
        </g>
      );

    case 'The Magician':
      return (
        <g stroke="#e8c356" fill="none" strokeWidth="1.2">
          {/* Lemniscate (infinity) */}
          <path d="M 30 30 q 8 -8 16 0 q 8 8 16 0 q 8 -8 16 0 q -8 8 -16 0 q -8 -8 -16 0 q -8 8 -16 0 Z"
                fill="#e8c356" opacity="0.7" />
          {/* Wand */}
          <line x1="50" y1="42" x2="50" y2="78" strokeWidth="1.8" />
          {/* Four elements at corners */}
          <circle cx="28" cy="60" r="3" fill="#e87fb1" stroke="none" />        {/* cup */}
          <rect x="68" y="57" width="6" height="6" fill="#f5d97a" stroke="none" /> {/* pentacle */}
          <path d="M 28 75 l 4 4 l -8 0 z" fill="#a78320" stroke="none" />     {/* sword */}
          <polygon points="71,78 74,72 77,78" fill="#e8c356" stroke="none" />  {/* wand */}
        </g>
      );

    case 'The High Priestess':
      return (
        <g stroke="#e8c356" fill="none" strokeWidth="1">
          {/* Two pillars */}
          <line x1="30" y1="25" x2="30" y2="80" strokeWidth="2" />
          <line x1="70" y1="25" x2="70" y2="80" strokeWidth="2" />
          <circle cx="30" cy="25" r="3" fill="#e8c356" />
          <circle cx="70" cy="25" r="3" fill="#e8c356" />
          {/* Moon crown */}
          <circle cx="50" cy="36" r="8" fill="#f5d97a" opacity="0.3" />
          <path d="M 44 36 A 6 6 0 1 0 44 36.001 Z" fill="#f5d97a" />
          {/* Veil pattern */}
          <path d="M 35 60 Q 50 50 65 60 L 65 78 L 35 78 Z" fill="#1e1740" />
          <path d="M 40 64 L 50 78 M 50 60 L 50 78 M 60 64 L 50 78"
                stroke="#e8c356" opacity="0.4" />
        </g>
      );

    case 'Wheel of Fortune':
      return (
        <g stroke="#e8c356" strokeWidth="1.2" fill="none">
          <circle cx="50" cy="50" r="22" />
          <circle cx="50" cy="50" r="14" />
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * Math.PI) / 4;
            return (
              <line key={i}
                    x1={50 + Math.cos(a) * 14}
                    y1={50 + Math.sin(a) * 14}
                    x2={50 + Math.cos(a) * 22}
                    y2={50 + Math.sin(a) * 22} />
            );
          })}
          <circle cx="50" cy="50" r="3" fill="#e8c356" />
        </g>
      );

    case 'The Fool':
      return (
        <g>
          {/* Mountain horizon */}
          <path d="M 15 70 L 35 50 L 50 65 L 70 45 L 85 70 Z" fill="#1e1740" stroke="#e8c356" strokeWidth="0.8" />
          {/* Sun */}
          <circle cx="68" cy="32" r="5" fill="#f5d97a" />
          {/* Cliff edge with figure */}
          <circle cx="35" cy="62" r="3" fill="#e8c356" />
          <line x1="35" y1="65" x2="35" y2="72" stroke="#e8c356" strokeWidth="1.2" />
          {/* Tiny dog */}
          <circle cx="42" cy="71" r="1.3" fill="#e8c356" />
        </g>
      );

    case 'The Empress':
      return (
        <g stroke="#e8c356" fill="none" strokeWidth="1">
          {/* Heart crown */}
          <circle cx="50" cy="35" r="8" fill="#e87fb1" opacity="0.3" />
          <path d="M 45 33 Q 40 28 45 25 Q 50 25 50 30 Q 50 25 55 25 Q 60 28 55 33 L 50 38 Z"
                fill="#e87fb1" />
          {/* Body / robe */}
          <path d="M 30 78 Q 35 55 50 50 Q 65 55 70 78 Z" fill="#1e1740" />
          {/* 12 stars halo */}
          {Array.from({ length: 9 }).map((_, i) => {
            const a = Math.PI - (i * Math.PI) / 8;
            return (
              <circle key={i} cx={50 + Math.cos(a) * 16} cy={35 + Math.sin(a) * 16} r="1" fill="#f5d97a" />
            );
          })}
        </g>
      );

    case 'The Emperor':
      return (
        <g stroke="#e8c356" strokeWidth="1.2" fill="none">
          {/* Throne */}
          <rect x="35" y="40" width="30" height="40" fill="#1e1740" />
          <line x1="32" y1="40" x2="35" y2="32" />
          <line x1="68" y1="40" x2="65" y2="32" />
          {/* Crown */}
          <path d="M 40 32 L 44 24 L 48 32 L 52 22 L 56 32 L 60 24 L 64 32" />
          {/* Ram heads (simplified spirals) */}
          <circle cx="40" cy="48" r="2" />
          <circle cx="60" cy="48" r="2" />
          {/* Ankh */}
          <circle cx="50" cy="58" r="3" />
          <line x1="50" y1="61" x2="50" y2="72" strokeWidth="1.4" />
          <line x1="46" y1="65" x2="54" y2="65" />
        </g>
      );

    case 'Justice':
      return (
        <g stroke="#e8c356" strokeWidth="1.1" fill="none">
          {/* Sword pointing up */}
          <line x1="50" y1="20" x2="50" y2="60" strokeWidth="1.8" />
          <line x1="44" y1="58" x2="56" y2="58" strokeWidth="1.8" />
          <polygon points="50,18 47,24 53,24" fill="#f5d97a" />
          {/* Scales beam */}
          <line x1="28" y1="50" x2="72" y2="50" strokeWidth="1.2" />
          {/* Suspension lines */}
          <line x1="30" y1="50" x2="30" y2="68" />
          <line x1="70" y1="50" x2="70" y2="68" />
          {/* Pans */}
          <path d="M 22 68 Q 30 76 38 68" />
          <path d="M 62 68 Q 70 76 78 68" />
          {/* Eye of balance */}
          <circle cx="50" cy="50" r="2.2" fill="#e8c356" />
        </g>
      );

    case 'The Hermit':
      return (
        <g stroke="#e8c356" strokeWidth="1" fill="none">
          {/* Lantern */}
          <rect x="62" y="34" width="14" height="14" fill="#1e1740" />
          <line x1="58" y1="48" x2="80" y2="48" />
          <line x1="62" y1="34" x2="76" y2="34" strokeWidth="1.4" />
          {/* Star inside lantern */}
          <g transform="translate(69 41)">
            <path d="M0 -4 L 1 -1 L 4 0 L 1 1 L 0 4 L -1 1 L -4 0 L -1 -1 Z" fill="#f5d97a" />
          </g>
          {/* Light glow */}
          <circle cx="69" cy="41" r="14" fill="#f5d97a" opacity="0.12" />
          {/* Hooded figure */}
          <path d="M 30 78 L 30 55 Q 30 42 40 38 Q 50 36 55 44 L 55 78 Z" fill="#1e1740" />
          <circle cx="38" cy="45" r="4" fill="#1e1740" />
          {/* Staff */}
          <line x1="22" y1="30" x2="32" y2="78" strokeWidth="1.6" />
        </g>
      );

    case 'The Devil':
      return (
        <g stroke="#e8c356" strokeWidth="1" fill="none">
          {/* Inverted pentagram */}
          <g transform="translate(50 38)">
            <path d="M 0 -12 L 11 8 L -11 -4 L 11 -4 L -11 8 Z" stroke="#e87fb1" strokeWidth="1.2" />
          </g>
          {/* Horned head */}
          <circle cx="50" cy="42" r="9" fill="#1e1740" />
          {/* Horns */}
          <path d="M 42 36 Q 38 28 35 32" strokeWidth="1.4" />
          <path d="M 58 36 Q 62 28 65 32" strokeWidth="1.4" />
          {/* Bat wings */}
          <path d="M 41 50 Q 20 50 22 70 Q 30 58 41 60 Z" fill="#2a2156" />
          <path d="M 59 50 Q 80 50 78 70 Q 70 58 59 60 Z" fill="#2a2156" />
          {/* Two chained figures below */}
          <circle cx="38" cy="74" r="2.5" fill="#a78320" />
          <circle cx="62" cy="74" r="2.5" fill="#a78320" />
          <line x1="40" y1="74" x2="60" y2="74" stroke="#a78320" strokeDasharray="1.5 1" />
        </g>
      );

    case 'Strength':
      return (
        <g stroke="#e8c356" strokeWidth="1" fill="none">
          {/* Lemniscate (infinity) crown */}
          <path d="M 38 22 q 6 -6 12 0 q 6 6 12 0 q -6 6 -12 0 q -6 -6 -12 0 Z"
                fill="#e8c356" opacity="0.7" />
          {/* Lion head */}
          <circle cx="60" cy="55" r="14" fill="#a78320" opacity="0.4" />
          <circle cx="60" cy="55" r="9" fill="#1e1740" />
          {/* Lion eyes */}
          <circle cx="57" cy="54" r="1" fill="#f5d97a" />
          <circle cx="63" cy="54" r="1" fill="#f5d97a" />
          {/* Mane rays */}
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * Math.PI) / 4;
            return (
              <line key={i}
                    x1={60 + Math.cos(a) * 14}
                    y1={55 + Math.sin(a) * 14}
                    x2={60 + Math.cos(a) * 19}
                    y2={55 + Math.sin(a) * 19}
                    strokeWidth="0.8" />
            );
          })}
          {/* Hand gently on jaw */}
          <path d="M 38 60 Q 48 65 52 60" strokeWidth="1.4" />
        </g>
      );

    case 'The Chariot':
      return (
        <g stroke="#e8c356" strokeWidth="1.1" fill="none">
          {/* Chariot body */}
          <rect x="30" y="48" width="40" height="22" fill="#1e1740" />
          {/* Wheels */}
          <circle cx="35" cy="74" r="6" />
          <line x1="29" y1="74" x2="41" y2="74" />
          <line x1="35" y1="68" x2="35" y2="80" />
          <circle cx="65" cy="74" r="6" />
          <line x1="59" y1="74" x2="71" y2="74" />
          <line x1="65" y1="68" x2="65" y2="80" />
          {/* Canopy with star */}
          <path d="M 28 48 L 50 32 L 72 48" />
          <circle cx="50" cy="40" r="2" fill="#f5d97a" />
          {/* Two sphinxes (abstracted) */}
          <circle cx="40" cy="56" r="3" fill="#f5d97a" />
          <circle cx="60" cy="56" r="3" fill="#e87fb1" />
          {/* Driver */}
          <circle cx="50" cy="52" r="2.5" fill="#e8c356" />
        </g>
      );

    case 'The Hierophant':
      return (
        <g stroke="#e8c356" strokeWidth="1" fill="none">
          {/* Triple-tiered crown */}
          <path d="M 42 25 L 58 25 L 56 32 L 44 32 Z" fill="#e8c356" />
          <path d="M 44 32 L 56 32 L 54 38 L 46 38 Z" fill="#e8c356" opacity="0.8" />
          <path d="M 46 38 L 54 38 L 52 44 L 48 44 Z" fill="#e8c356" opacity="0.6" />
          {/* Face */}
          <circle cx="50" cy="50" r="4" fill="#1e1740" />
          {/* Robe */}
          <path d="M 32 80 L 38 56 Q 50 50 62 56 L 68 80 Z" fill="#1e1740" />
          {/* Two crossed keys at bottom */}
          <circle cx="44" cy="78" r="2" />
          <line x1="45" y1="78" x2="56" y2="68" />
          <circle cx="56" cy="78" r="2" />
          <line x1="55" y1="78" x2="44" y2="68" />
          {/* Blessing hand */}
          <line x1="50" y1="55" x2="50" y2="65" strokeWidth="1.4" />
          <line x1="48" y1="56" x2="48" y2="63" />
        </g>
      );

    case 'Temperance':
      return (
        <g stroke="#e8c356" strokeWidth="1" fill="none">
          {/* Angel halo */}
          <circle cx="50" cy="28" r="6" fill="#f5d97a" opacity="0.3" />
          <circle cx="50" cy="28" r="3" fill="#f5d97a" />
          {/* Body */}
          <path d="M 38 76 L 38 50 Q 50 42 62 50 L 62 76 Z" fill="#1e1740" />
          {/* Wings */}
          <path d="M 38 50 Q 22 48 25 60 Q 32 56 38 58" fill="#2a2156" />
          <path d="M 62 50 Q 78 48 75 60 Q 68 56 62 58" fill="#2a2156" />
          {/* Two cups with arc of water */}
          <path d="M 35 60 L 32 65 L 38 65 Z" fill="#e8c356" />
          <path d="M 65 60 L 62 65 L 68 65 Z" fill="#e8c356" />
          <path d="M 36 64 Q 50 56 64 64" stroke="#a78320" strokeDasharray="1.5 1.5" />
          {/* Square + triangle on chest (alchemy) */}
          <rect x="46" y="56" width="8" height="8" />
          <path d="M 50 57 L 53 63 L 47 63 Z" fill="#e8c356" />
        </g>
      );

    case 'Judgement':
      return (
        <g stroke="#e8c356" strokeWidth="1" fill="none">
          {/* Angel head + halo */}
          <circle cx="50" cy="28" r="6" fill="#f5d97a" opacity="0.3" />
          <circle cx="50" cy="28" r="3.5" fill="#f5d97a" />
          {/* Trumpet */}
          <line x1="50" y1="30" x2="76" y2="40" strokeWidth="1.6" />
          <path d="M 76 40 L 80 36 L 80 44 Z" fill="#e8c356" />
          {/* Cross banner on trumpet */}
          <rect x="60" y="32" width="6" height="5" fill="#fff6c7" />
          <line x1="63" y1="32" x2="63" y2="37" stroke="#e87fb1" />
          <line x1="60" y1="34.5" x2="66" y2="34.5" stroke="#e87fb1" />
          {/* Three rising figures */}
          <circle cx="30" cy="60" r="3" fill="#1e1740" />
          <path d="M 26 78 L 26 65 Q 30 62 34 65 L 34 78" fill="#1e1740" />
          <circle cx="50" cy="56" r="3.5" fill="#1e1740" />
          <path d="M 45 78 L 45 62 Q 50 58 55 62 L 55 78" fill="#1e1740" />
          <circle cx="70" cy="60" r="3" fill="#1e1740" />
          <path d="M 66 78 L 66 65 Q 70 62 74 65 L 74 78" fill="#1e1740" />
        </g>
      );

    case 'The World':
      return (
        <g stroke="#e8c356" strokeWidth="1" fill="none">
          {/* Laurel wreath */}
          <ellipse cx="50" cy="50" rx="22" ry="28" strokeWidth="1.4" />
          {/* Wreath details — small leaves */}
          {Array.from({ length: 14 }).map((_, i) => {
            const a = (i * 2 * Math.PI) / 14;
            const x = 50 + Math.cos(a) * 22;
            const y = 50 + Math.sin(a) * 28;
            return <circle key={i} cx={x} cy={y} r="1.2" fill="#e8c356" />;
          })}
          {/* Central dancing figure */}
          <circle cx="50" cy="42" r="3" fill="#f5d97a" />
          <line x1="50" y1="45" x2="50" y2="58" strokeWidth="1.4" />
          <line x1="50" y1="50" x2="42" y2="55" />
          <line x1="50" y1="50" x2="58" y2="45" />
          <line x1="50" y1="58" x2="46" y2="68" />
          <line x1="50" y1="58" x2="54" y2="68" />
          {/* 4 corner symbols (man, eagle, lion, bull simplified) */}
          <circle cx="22" cy="22" r="2.5" fill="#e87fb1" />
          <circle cx="78" cy="22" r="2.5" fill="#f5d97a" />
          <circle cx="22" cy="82" r="2.5" fill="#a78320" />
          <circle cx="78" cy="82" r="2.5" fill="#e8c356" />
        </g>
      );

    case 'The Hanged Man':
      return (
        <g stroke="#e8c356" strokeWidth="1.1" fill="none">
          {/* T-shaped wooden cross */}
          <line x1="20" y1="26" x2="80" y2="26" strokeWidth="1.8" />
          <line x1="50" y1="22" x2="50" y2="34" />
          {/* Rope */}
          <line x1="50" y1="26" x2="50" y2="38" strokeDasharray="1.5 1.5" />
          {/* Figure upside down (head at bottom now) */}
          {/* Body */}
          <line x1="50" y1="38" x2="50" y2="62" strokeWidth="1.6" />
          {/* Head with halo (still glowing — enlightenment) */}
          <circle cx="50" cy="68" r="6" fill="#f5d97a" opacity="0.3" />
          <circle cx="50" cy="68" r="3.5" fill="#1e1740" stroke="#e8c356" />
          {/* Arms behind back (X) */}
          <line x1="42" y1="46" x2="58" y2="46" />
          {/* One leg bent in figure-4 */}
          <line x1="50" y1="50" x2="40" y2="58" />
          <line x1="40" y1="58" x2="50" y2="58" />
        </g>
      );

    default: {
      // Try to procedurally render a Minor Arcana card from its name.
      const minor = renderMinorArcana(name);
      if (minor) return minor;
      // Generic mandala fallback for anything we still can't recognise.
      return (
        <g stroke="#e8c356" fill="none" strokeWidth="1">
          <circle cx="50" cy="50" r="22" />
          <circle cx="50" cy="50" r="14" opacity="0.7" />
          <circle cx="50" cy="50" r="6" fill="#e8c356" />
          {Array.from({ length: 6 }).map((_, i) => {
            const a = (i * Math.PI) / 3;
            return (
              <line key={i}
                    x1={50 + Math.cos(a) * 6}
                    y1={50 + Math.sin(a) * 6}
                    x2={50 + Math.cos(a) * 22}
                    y2={50 + Math.sin(a) * 22} />
            );
          })}
          {Array.from({ length: 6 }).map((_, i) => {
            const a = (i * Math.PI) / 3 + Math.PI / 6;
            return (
              <circle key={`d${i}`}
                      cx={50 + Math.cos(a) * 18}
                      cy={50 + Math.sin(a) * 18}
                      r="1.5" fill="#e8c356" />
            );
          })}
        </g>
      );
    }
  }
}


// ─── Minor Arcana procedural art ────────────────────────────────────────────
// Backend names follow the pattern "{Rank} of {Suit}", e.g. "Two of Wands",
// "Queen of Cups". Pip cards (Ace + 2-10) are rendered by placing N copies
// of the suit symbol in a rank-specific layout; court cards (Page/Knight/
// Queen/King) get a stylised figure plus the suit emblem.

type Suit = 'Wands' | 'Cups' | 'Swords' | 'Pentacles';

const PIP_COUNT: Record<string, number> = {
  Ace: 1, Two: 2, Three: 3, Four: 4, Five: 5,
  Six: 6, Seven: 7, Eight: 8, Nine: 9, Ten: 10,
};

const COURT_RANKS = new Set(['Page', 'Knight', 'Queen', 'King']);

// Suit-specific colour palette so each masti reads at a glance.
const SUIT_COLOR: Record<Suit, { primary: string; accent: string; soft: string }> = {
  Wands:     { primary: '#e89647', accent: '#f5d97a', soft: '#a85a1a' },
  Cups:      { primary: '#e87fb1', accent: '#9fb9f0', soft: '#6a3a78' },
  Swords:    { primary: '#cfd8e6', accent: '#e8c356', soft: '#5a6678' },
  Pentacles: { primary: '#f5d97a', accent: '#e8c356', soft: '#7a5a14' },
};

/** Rank-specific positions for N pip symbols, centred in a 100×100 viewport. */
function pipPositions(n: number): Array<[number, number]> {
  switch (n) {
    case 1: return [[50, 50]];
    case 2: return [[50, 32], [50, 68]];
    case 3: return [[50, 28], [32, 62], [68, 62]];
    case 4: return [[32, 32], [68, 32], [32, 68], [68, 68]];
    case 5: return [[32, 32], [68, 32], [50, 50], [32, 68], [68, 68]];
    case 6: return [[32, 28], [68, 28], [32, 50], [68, 50], [32, 72], [68, 72]];
    case 7: return [[32, 26], [68, 26], [32, 48], [68, 48], [50, 38], [32, 70], [68, 70]];
    case 8: return [
      [30, 24], [70, 24], [30, 42], [70, 42],
      [30, 58], [70, 58], [30, 76], [70, 76],
    ];
    case 9: return [
      [28, 28], [50, 28], [72, 28],
      [28, 50], [50, 50], [72, 50],
      [28, 72], [50, 72], [72, 72],
    ];
    case 10: return [
      [28, 22], [50, 22], [72, 22],
      [36, 38], [64, 38],
      [36, 60], [64, 60],
      [28, 76], [50, 76], [72, 76],
    ];
    default: return [];
  }
}


/** A single instance of the suit symbol, drawn at (cx, cy) with given radius. */
function PipSymbol({
  suit, cx, cy, r,
}: { suit: Suit; cx: number; cy: number; r: number }) {
  const c = SUIT_COLOR[suit];
  const key = `${suit}-${cx}-${cy}`;
  switch (suit) {
    case 'Wands':
      return (
        <g key={key} transform={`translate(${cx} ${cy})`}>
          {/* Wooden staff */}
          <line x1="0" y1={-r * 0.95} x2="0" y2={r * 0.95}
                stroke={c.soft} strokeWidth={r * 0.28} strokeLinecap="round" />
          {/* Leaves sprouting */}
          <path d={`M 0 ${-r * 0.6} q ${r * 0.5} ${-r * 0.1} ${r * 0.55} ${r * 0.35}`}
                fill="none" stroke={c.primary} strokeWidth={r * 0.18} strokeLinecap="round" />
          <path d={`M 0 ${-r * 0.6} q ${-r * 0.5} ${-r * 0.1} ${-r * 0.55} ${r * 0.35}`}
                fill="none" stroke={c.primary} strokeWidth={r * 0.18} strokeLinecap="round" />
          {/* Tip flame */}
          <circle cx="0" cy={-r * 0.95} r={r * 0.22} fill={c.accent} />
        </g>
      );
    case 'Cups':
      return (
        <g key={key} transform={`translate(${cx} ${cy})`}>
          {/* Chalice bowl */}
          <path
            d={`M ${-r * 0.6} ${-r * 0.2} Q ${-r * 0.6} ${r * 0.4} 0 ${r * 0.45} Q ${r * 0.6} ${r * 0.4} ${r * 0.6} ${-r * 0.2} Z`}
            fill={c.primary} stroke={c.accent} strokeWidth={r * 0.08} />
          {/* Stem */}
          <line x1="0" y1={r * 0.45} x2="0" y2={r * 0.8}
                stroke={c.accent} strokeWidth={r * 0.16} strokeLinecap="round" />
          {/* Base */}
          <line x1={-r * 0.35} y1={r * 0.85} x2={r * 0.35} y2={r * 0.85}
                stroke={c.accent} strokeWidth={r * 0.18} strokeLinecap="round" />
          {/* Inner gem */}
          <circle cx="0" cy={r * 0.05} r={r * 0.18} fill={c.accent} opacity="0.85" />
        </g>
      );
    case 'Swords':
      return (
        <g key={key} transform={`translate(${cx} ${cy})`}>
          {/* Blade */}
          <path d={`M 0 ${-r * 0.95} L ${r * 0.15} ${r * 0.35} L ${-r * 0.15} ${r * 0.35} Z`}
                fill={c.primary} stroke={c.accent} strokeWidth={r * 0.08} />
          {/* Crossguard */}
          <line x1={-r * 0.55} y1={r * 0.35} x2={r * 0.55} y2={r * 0.35}
                stroke={c.accent} strokeWidth={r * 0.16} strokeLinecap="round" />
          {/* Hilt */}
          <line x1="0" y1={r * 0.35} x2="0" y2={r * 0.8}
                stroke={c.soft} strokeWidth={r * 0.22} strokeLinecap="round" />
          {/* Pommel */}
          <circle cx="0" cy={r * 0.88} r={r * 0.15} fill={c.accent} />
        </g>
      );
    case 'Pentacles':
      return (
        <g key={key} transform={`translate(${cx} ${cy})`}>
          {/* Disc */}
          <circle r={r * 0.9} fill={c.primary} stroke={c.accent} strokeWidth={r * 0.1} />
          <circle r={r * 0.9} fill="none" stroke={c.soft} strokeWidth={r * 0.05} opacity="0.7" />
          {/* 5-point star inside */}
          <g>
            {Array.from({ length: 5 }).map((_, i) => {
              const a = (i * 2 * Math.PI) / 5 - Math.PI / 2;
              const nx = Math.cos(a) * r * 0.55;
              const ny = Math.sin(a) * r * 0.55;
              const a2 = a + Math.PI / 5;
              const mx = Math.cos(a2) * r * 0.22;
              const my = Math.sin(a2) * r * 0.22;
              return <path key={i} d={`M ${nx} ${ny} L ${mx} ${my}`}
                           stroke={c.soft} strokeWidth={r * 0.08} />;
            })}
          </g>
        </g>
      );
  }
}


/** Stylised court figure with the suit emblem held above. */
function CourtFigure({ suit, rank }: { suit: Suit; rank: 'Page' | 'Knight' | 'Queen' | 'King' }) {
  const c = SUIT_COLOR[suit];
  // Per-rank silhouette tweaks: Page slim, Knight on horse hint, Queen flowing
  // robe + crown, King broad throne. All share the same emblem slot at top.
  const isKing = rank === 'King';
  const isQueen = rank === 'Queen';
  const isKnight = rank === 'Knight';

  return (
    <g>
      {/* Halo / aura */}
      <circle cx="50" cy="40" r="22" fill={c.primary} opacity="0.15" />

      {/* Crown / hood */}
      {(isKing || isQueen) ? (
        <path d="M 38 28 L 42 20 L 46 28 L 50 18 L 54 28 L 58 20 L 62 28 Z"
              fill={c.accent} stroke="#a78320" strokeWidth="0.6" />
      ) : (
        <path d="M 40 30 Q 50 22 60 30 L 58 36 L 42 36 Z"
              fill={c.soft} stroke={c.accent} strokeWidth="0.6" />
      )}

      {/* Head */}
      <circle cx="50" cy="40" r="6" fill="#1e1740" stroke={c.accent} strokeWidth="0.6" />

      {/* Body / robe */}
      {isQueen ? (
        <path d="M 30 82 Q 32 58 50 50 Q 68 58 70 82 Z"
              fill={c.primary} stroke={c.accent} strokeWidth="0.6" opacity="0.85" />
      ) : isKing ? (
        <>
          {/* Throne back */}
          <rect x="28" y="50" width="44" height="34" fill="#1e1740"
                stroke={c.accent} strokeWidth="0.6" />
          {/* Robe */}
          <path d="M 36 82 L 40 54 Q 50 50 60 54 L 64 82 Z"
                fill={c.primary} stroke={c.accent} strokeWidth="0.6" />
        </>
      ) : isKnight ? (
        <>
          {/* Horse hint — flowing curve under figure */}
          <path d="M 22 82 Q 30 70 50 72 Q 70 70 78 82"
                fill={c.soft} stroke={c.accent} strokeWidth="0.6" />
          <path d="M 38 80 L 38 56 Q 50 50 62 56 L 62 80 Z"
                fill={c.primary} stroke={c.accent} strokeWidth="0.6" />
        </>
      ) : (
        <path d="M 38 82 L 40 56 Q 50 50 60 56 L 62 82 Z"
              fill={c.primary} stroke={c.accent} strokeWidth="0.6" />
      )}

      {/* Suit emblem held aloft */}
      <g transform="translate(76 30)">
        <PipSymbol suit={suit} cx={0} cy={0} r={7} />
      </g>

      {/* Decorative belt */}
      <line x1="40" y1="62" x2="60" y2="62" stroke={c.accent} strokeWidth="0.5" opacity="0.7" />
    </g>
  );
}


const MINOR_RANK_LABEL: Record<string, string> = {
  Ace: 'I', Two: 'II', Three: 'III', Four: 'IV', Five: 'V',
  Six: 'VI', Seven: 'VII', Eight: 'VIII', Nine: 'IX', Ten: 'X',
  Page: 'Page', Knight: 'Knight', Queen: 'Queen', King: 'King',
};

function minorRankLabel(name?: string): string {
  if (!name) return '';
  const m = /^(\w+) of (Wands|Cups|Swords|Pentacles)$/.exec(name);
  if (!m) return '';
  return MINOR_RANK_LABEL[m[1]] ?? '';
}


function renderMinorArcana(name?: string): JSX.Element | null {
  if (!name) return null;
  const m = /^(\w+) of (Wands|Cups|Swords|Pentacles)$/.exec(name);
  if (!m) return null;
  const rank = m[1];
  const suit = m[2] as Suit;

  // Court card branch.
  if (COURT_RANKS.has(rank)) {
    return <CourtFigure suit={suit} rank={rank as 'Page' | 'Knight' | 'Queen' | 'King'} />;
  }

  // Pip card branch.
  const n = PIP_COUNT[rank];
  if (!n) return null;

  const positions = pipPositions(n);
  // Symbol radius shrinks as count grows so the layout breathes.
  const r = n === 1 ? 22 : n <= 3 ? 14 : n <= 5 ? 11 : n <= 7 ? 9.5 : 8;

  const c = SUIT_COLOR[suit];

  return (
    <g>
      {/* Soft suit-coloured glow behind the layout */}
      <circle cx="50" cy="50" r="38" fill={c.primary} opacity="0.08" />
      {/* Faint ring framing the pips */}
      <circle cx="50" cy="50" r="36" fill="none" stroke={c.accent}
              strokeWidth="0.4" opacity="0.5" strokeDasharray="2 2" />
      {positions.map(([x, y], i) => (
        <PipSymbol key={i} suit={suit} cx={x} cy={y} r={r} />
      ))}
    </g>
  );
}


export default function TarotCard({ card, size = 220, noIntro = false }: Props) {
  const { t, i18n } = useTranslation();
  const c = (card ?? {}) as CardDto;
  const lang = i18n.language.startsWith('ru') ? 'ru' : 'en';
  const nameLocal = (lang === 'ru' ? c.name_ru : c.name_en) || c.name_en || c.name_ru || '???';
  const numeral = ROMAN[c.name_en ?? ''] ?? minorRankLabel(c.name_en);
  const reversed = c.orientation === 'reversed';

  const w = size;
  // Aspect ratio matches the actual card asset files in /public/cards/
  // (all generated at 768×1028 → height/width = 1.339). Previously this
  // was 1.55, which forced object-fit:cover to crop ~8% off each side
  // of every card image — most visible on the deck back where the gold
  // frame runs right to the edge. SVG fallback ends up slightly
  // letter-boxed inside the new aspect — acceptable since real images
  // load 99% of the time.
  const h = Math.round(size * (1028 / 768));

  // If we have a real illustration for this card in /public/cards/,
  // render the JPEG instead of the procedural SVG. The JPEG already
  // contains its own gold frame, Roman numeral, and ornaments — so
  // we just display the image (rotated 180° for reversed cards) and
  // skip the SVG composition entirely.
  // Fallback to SVG happens automatically via onError if the file
  // doesn't exist (e.g. a Swords or Pentacles card not yet generated).
  //
  // Telegram's mobile WebView occasionally throttles concurrent image
  // loads on a spread of 5-10 cards, so the first attempt can fail for
  // 2-3 of them with no real problem. We retry once after 1.5 seconds
  // before giving up to SVG, which empirically clears 95% of these
  // false negatives.
  const imgPath = cardImagePath(c.name_en);
  const [imgFailed, setImgFailed] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [retryAttempted, setRetryAttempted] = useState(false);
  const showImage = imgPath && !imgFailed;
  // Append a cache-buster on the retry attempt so the browser doesn't
  // hand us back the cached failed response. Real requests after the
  // first one are rare, so the perf hit is negligible.
  const imgSrc = imgPath
    ? (retryNonce > 0 ? `${imgPath}?r=${retryNonce}` : imgPath)
    : '';

  if (showImage) {
    return (
      <motion.div
        initial={noIntro ? false : { rotateY: 180, opacity: 0 }}
        animate={noIntro ? undefined : { rotateY: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
        className="mx-auto select-none"
        style={{ width: w, height: h }}
      >
        <img
          src={imgSrc}
          alt={nameLocal}
          width={w}
          height={h}
          onError={() => {
            // Retry once after a short delay before giving up to SVG.
            // Covers WebView throttling and transient network blips.
            if (!retryAttempted) {
              setRetryAttempted(true);
              setTimeout(() => setRetryNonce((n) => n + 1), 1500);
            } else {
              setImgFailed(true);
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '6px',
            transform: reversed ? 'rotate(180deg)' : undefined,
          }}
        />
        {/* No external "↻ ПЕРЕВЁРНУТАЯ" caption in image mode — the
            180° rotation is the visual cue, and the extra <p> was
            colliding with position labels in spreads (Themed, etc.). */}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={noIntro ? false : { rotateY: 180, opacity: 0 }}
      animate={noIntro ? undefined : { rotateY: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: 'easeOut' }}
      className="mx-auto select-none"
      style={{ width: w, height: h }}
    >
      <svg
        viewBox="0 0 100 155"
        width={w}
        height={h}
        style={{ transform: reversed ? 'rotate(180deg)' : undefined }}
      >
        <defs>
          <linearGradient id={`card-bg-${size}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e1740" />
            <stop offset="100%" stopColor="#0b0820" />
          </linearGradient>
          <pattern id={`card-stars-${size}`} width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="3" cy="4" r="0.4" fill="#e8c356" opacity="0.5" />
            <circle cx="15" cy="11" r="0.3" fill="#e8c356" opacity="0.4" />
            <circle cx="8" cy="17" r="0.4" fill="#e8c356" opacity="0.5" />
          </pattern>
        </defs>

        {/* Card body */}
        <rect x="2" y="2" width="96" height="151" rx="6"
              fill={`url(#card-bg-${size})`} stroke="#a78320" strokeWidth="0.8" />
        {/* Star pattern overlay */}
        <rect x="2" y="2" width="96" height="151" rx="6" fill={`url(#card-stars-${size})`} opacity="0.6" />

        {/* Inner gold frame */}
        <rect x="6" y="6" width="88" height="143" rx="3"
              fill="none" stroke="#e8c356" strokeWidth="0.4" opacity="0.7" />

        {/* Corner flourishes */}
        {[
          { x: 8, y: 8, r: 0 },
          { x: 92, y: 8, r: 90 },
          { x: 92, y: 147, r: 180 },
          { x: 8, y: 147, r: 270 },
        ].map((c, i) => (
          <g key={i} transform={`translate(${c.x} ${c.y}) rotate(${c.r})`}>
            <path d="M 0 4 Q 0 0 4 0 M 0 0 L 2 2" fill="none" stroke="#e8c356" strokeWidth="0.6" />
            <circle cx="3" cy="3" r="0.6" fill="#e8c356" />
          </g>
        ))}

        {/* Roman numeral */}
        <text x="50" y="22" textAnchor="middle"
              fontFamily="'Cormorant Garamond', Georgia, serif"
              fontSize="9" fill="#e8c356" letterSpacing="1.5">
          {numeral}
        </text>

        {/* Decorative top divider */}
        <line x1="20" y1="26" x2="80" y2="26" stroke="#e8c356" strokeWidth="0.3" opacity="0.6" />
        <circle cx="50" cy="26" r="0.8" fill="#e8c356" />

        {/* Central art */}
        <g transform="translate(0 32) scale(1 0.85)">
          <CardArt name={c.name_en} />
        </g>

        {/* Decorative bottom divider */}
        <line x1="20" y1="125" x2="80" y2="125" stroke="#e8c356" strokeWidth="0.3" opacity="0.6" />
        <circle cx="50" cy="125" r="0.8" fill="#e8c356" />

        {/* Card name */}
        <text x="50" y="140" textAnchor="middle"
              fontFamily="'Cormorant Garamond', Georgia, serif"
              fontSize="7.5" fontStyle="italic" fill="#f5d97a">
          {nameLocal}
        </text>

        {reversed && (
          <text x="50" y="148" textAnchor="middle"
                fontSize="3.5" fill="#e87fb1" letterSpacing="0.8">
            ↻ {t('card.reversed')}
          </text>
        )}
      </svg>
    </motion.div>
  );
}
